import { useMemo } from "react";
import { Moon, Coffee, Sun, Briefcase, Wind, Glasses } from "lucide-react";

const KIND_META = {
  shift: { label: "Shift", color: "bg-emerald-500/70 border-emerald-400/60", icon: Briefcase, text: "text-emerald-100" },
  anchor_sleep: { label: "Anchor Sleep", color: "bg-indigo-500/70 border-indigo-400/60", icon: Moon, text: "text-indigo-100" },
  caffeine_cutoff: { label: "Caffeine Cutoff", color: "bg-rose-500/50 border-rose-400/60", icon: Coffee, text: "text-rose-100" },
  wind_down: { label: "Wind Down", color: "bg-violet-500/60 border-violet-400/50", icon: Wind, text: "text-violet-100" },
  light_exposure: { label: "Bright Light", color: "bg-amber-500/70 border-amber-400/60", icon: Sun, text: "text-amber-100" },
  dark_glasses: { label: "Dark Glasses", color: "bg-slate-500/60 border-slate-400/50", icon: Glasses, text: "text-slate-100" },
};

export default function AdaptiveTimeline({ plan, standalone }) {
  const blocks = plan?.blocks || [];
  const now = Date.now();
  const dayStart = useMemo(() => {
    const d = new Date();
    d.setMinutes(0, 0, 0);
    d.setHours(d.getHours() - 2);
    return d.getTime();
  }, []);
  const dayEnd = dayStart + 72 * 3600 * 1000;

  const positioned = blocks
    .map((b) => {
      const s = new Date(b.start).getTime();
      const e = new Date(b.end).getTime();
      if (e < dayStart || s > dayEnd) return null;
      const left = Math.max(0, ((s - dayStart) / (dayEnd - dayStart)) * 100);
      const width = Math.max(0.8, ((Math.min(e, dayEnd) - Math.max(s, dayStart)) / (dayEnd - dayStart)) * 100);
      return { ...b, left, width };
    })
    .filter(Boolean);

  const hours = Array.from({ length: 13 }).map((_, i) => {
    const t = new Date(dayStart + i * 6 * 3600 * 1000);
    return t;
  });

  const rows = ["shift", "anchor_sleep", "wind_down", "caffeine_cutoff", "light_exposure", "dark_glasses"];

  return (
    <section className={standalone ? "space-y-4" : "space-y-4"} data-testid="timeline-24h-container">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Adaptive Timeline</h2>
          <p className="text-slate-400 text-sm mt-1">Next 72 hours · anchor sleep, caffeine, and light are recomputed live from your shifts.</p>
        </div>
        <div className="hidden sm:flex items-center gap-2 flex-wrap">
          {Object.entries(KIND_META).map(([k, v]) => (
            <span key={k} className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-mono-ui text-slate-400">
              <span className={`w-2 h-2 rounded-full ${v.color.split(" ")[0]}`} />
              {v.label}
            </span>
          ))}
        </div>
      </div>

      <div className="circa-card p-5 sm:p-6">
        {/* Hour axis */}
        <div className="relative h-6 border-b border-white/10">
          {hours.map((h, i) => (
            <div key={i} className="absolute -translate-x-1/2 text-[10px] uppercase tracking-widest font-mono-ui text-slate-500" style={{ left: `${(i / 12) * 100}%` }}>
              {h.toLocaleString(undefined, { weekday: "short" }).slice(0, 3)} {h.getHours().toString().padStart(2, "0")}
            </div>
          ))}
        </div>

        {/* Now marker */}
        <div className="relative">
          {rows.map((kind) => {
            const meta = KIND_META[kind];
            const rowBlocks = positioned.filter((p) => p.kind === kind);
            return (
              <div key={kind} className="relative h-11 mt-3">
                <div className="absolute inset-0 flex items-center">
                  <div className="text-[10px] uppercase tracking-widest font-mono-ui text-slate-500 w-28 shrink-0 flex items-center gap-1.5">
                    <meta.icon className="w-3 h-3" /> {meta.label}
                  </div>
                  <div className="relative flex-1 h-full">
                    <div className="absolute inset-y-3 left-0 right-0 rounded-full bg-slate-900/60 border border-white/5" />
                    {/* now marker */}
                    <div
                      className="absolute top-0 bottom-0 w-px bg-indigo-400/70"
                      style={{ left: `${Math.max(0, Math.min(100, ((now - dayStart) / (dayEnd - dayStart)) * 100))}%` }}
                    />
                    {rowBlocks.map((b, i) => (
                      <div
                        key={i}
                        className={`tl-block absolute inset-y-2 rounded-lg border ${meta.color} shadow-md`}
                        style={{ left: `${b.left}%`, width: `${b.width}%` }}
                        title={`${meta.label} · ${new Date(b.start).toLocaleTimeString()} → ${new Date(b.end).toLocaleTimeString()}`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {positioned.length === 0 && (
          <div className="text-center text-slate-500 py-8 text-sm">Add a shift to see your adaptive plan.</div>
        )}
      </div>
    </section>
  );
}
